import type { EntityType } from "@config";
import type { Repeatable } from "./types";
import {
  collectNamespacedFieldKeys,
  reparentSettingsTokens,
  wrapSettingsTokens,
} from "./wrap-tokens";
import type { KnownVariable } from "./infer-fields";
import { diffChangedSettings } from "./diff-settings";
import { deriveRepeatableFields } from "./derive-fields";
import { scanSubtreeTokens } from "./scan-subtree-tokens";
import type { RepeatableField } from "./types";

export interface PlanRepeatableInput {
  entityId: string;
  key: string;
  name: string;
  subtreeEntities: Record<string, EntityType>;
  allEntities: Record<string, EntityType>;
  knownVariables: KnownVariable[];
  /** Author-seeded fallback rows collected by the "Add repeatable group"
   * dialog's predefined-data section. Defaults to empty. */
  predefined?: Record<string, string>[];
  /** Nearest group ABOVE this entity, whose tokens inside the subtree re-park
   * down into the new group (see findAncestorRepeatableKey). */
  ancestorKey?: string | null;
  /** The ancestor group's field definitions, so a re-parked field keeps its
   * label and type instead of being re-inferred. */
  ancestorFields?: RepeatableField[];
}

export interface EntityPatch {
  id: string;
  settings: Record<string, unknown>;
}

export interface PlanRepeatableResult {
  repeatable: Repeatable;
  entityPatches: EntityPatch[];
  /** Fields taken over from the ancestor group, and whether the ancestor still
   * uses each one elsewhere (outside this subtree) and must therefore keep it. */
  reparked: { key: string; stillUsedByAncestor: boolean }[];
}

export const planRepeatable = (
  input: PlanRepeatableInput,
): PlanRepeatableResult => {
  const {
    entityId,
    key,
    name,
    subtreeEntities,
    allEntities,
    knownVariables,
    predefined,
  } = input;

  const derivedFields = deriveRepeatableFields(subtreeEntities, knownVariables);
  const fieldNameSet = new Set(derivedFields.map((f) => f.key));

  // Tokens already owned by the ancestor group but sitting INSIDE this subtree
  // re-park down: the nearest enclosing group owns a token. They keep the
  // ancestor's label/type so the share dialog column reads the same.
  const ancestorKey = input.ancestorKey ?? null;
  const reparkedKeys = ancestorKey
    ? Array.from(
        new Set(
          Object.values(subtreeEntities).flatMap((entity) =>
            collectNamespacedFieldKeys(entity.settings ?? {}, ancestorKey),
          ),
        ),
      ).sort()
    : [];

  const ancestorFieldByKey = new Map(
    (input.ancestorFields ?? []).map((field) => [field.key, field]),
  );
  const reparkedFields: RepeatableField[] = reparkedKeys
    .filter((fieldKey) => !fieldNameSet.has(fieldKey))
    .map(
      (fieldKey) =>
        ancestorFieldByKey.get(fieldKey) ?? {
          key: fieldKey,
          name: fieldKey,
          type: "input",
        },
    );
  const fields = [...derivedFields, ...reparkedFields];

  // Does the ancestor still reference a re-parked field OUTSIDE this subtree?
  // If so it keeps its own column; the two are then separate scopes.
  const outsideScan = ancestorKey
    ? scanSubtreeTokens(subtreeEntities, allEntities)
    : null;
  const alsoOutside = new Set(outsideScan?.alsoOutside ?? []);
  const reparked = reparkedKeys.map((fieldKey) => ({
    key: fieldKey,
    stillUsedByAncestor: alsoOutside.has(`${ancestorKey}.${fieldKey}`),
  }));

  const patches: EntityPatch[] = [];
  for (const entity of Object.values(subtreeEntities)) {
    const settings = entity.settings ?? {};
    let next = settings;
    if (fieldNameSet.size > 0) {
      next = wrapSettingsTokens(next, key, fieldNameSet);
    }
    if (ancestorKey && reparkedKeys.length > 0) {
      next = reparentSettingsTokens(next, ancestorKey, key);
    }
    // keep only the settings props that actually changed
    const changed = diffChangedSettings(settings, next);
    if (Object.keys(changed).length > 0)
      patches.push({ id: entity.id, settings: changed });
  }

  // root always gets the repeatableKey marker (merge into an existing patch if present)
  const rootPatch = patches.find((p) => p.id === entityId);
  if (rootPatch) rootPatch.settings.repeatableKey = key;
  else patches.push({ id: entityId, settings: { repeatableKey: key } });

  const repeatable: Repeatable = {
    key,
    name,
    entityId,
    fields,
    predefined: predefined ?? [],
  };
  return {
    repeatable,
    entityPatches: patches,
    reparked,
  };
};
